/**
 * Hook for subscribing to API sequence generation events via EventSource.
 *
 * @module hooks/useApiSequenceGenerationStream
 *
 * Connects to the broker-backed SSE endpoint with automatic reconnection
 * and cached event replay. Survives navigation and browser refresh.
 */

import React from 'react';
import { API_URL } from '../config';
import type { ApiSequenceConfig } from '@chatsdet/types';

export type AgentEventType = 'progress' | 'tool-call' | 'tool-result' | 'text' | 'complete' | 'error';

export type AgentEvent = {
  type: AgentEventType;
  timestamp: number;
  toolName?: string;
  args?: unknown;
  result?: unknown;
  success?: boolean;
  delta?: string;
  message?: string;
  config?: ApiSequenceConfig;
  summary?: string;
  propertyId?: string | null;
  credentialId?: string | null;
};

type GenerateStatus = 'idle' | 'running' | 'complete' | 'error';

type ConnectionState = 'connected' | 'reconnecting' | 'failed';

const MAX_RECONNECT_ATTEMPTS = 5;

type StartGenerateOptions = {
  endpoint: string;
  body: Record<string, unknown>;
};

export type ApiSequenceGenerationStream = {
  events: AgentEvent[];
  status: GenerateStatus;
  finalConfig: ApiSequenceConfig | null;
  summary: string | null;
  connectionState: ConnectionState;
  /** Start a generation via POST, then subscribe to the stream */
  startGeneration: (options: StartGenerateOptions) => void;
  /** Cancel an in-progress generation */
  cancel: () => void;
  /** Reset all state */
  reset: () => void;
};

/**
 * Subscribe to an API sequence generation stream.
 *
 * Can be used in two ways:
 * 1. Pass `generationId` directly to reconnect to an existing generation
 * 2. Call `startGeneration()` to POST and begin a new generation
 */
export function useApiSequenceGenerationStream(
  initialGenerationId?: string | null
): ApiSequenceGenerationStream {
  const [events, setEvents] = React.useState<AgentEvent[]>([]);
  const [status, setStatus] = React.useState<GenerateStatus>(
    initialGenerationId ? 'running' : 'idle'
  );
  const [finalConfig, setFinalConfig] = React.useState<ApiSequenceConfig | null>(null);
  const [summary, setSummary] = React.useState<string | null>(null);
  const [connectionState, setConnectionState] = React.useState<ConnectionState>('connected');
  const [generationId, setGenerationId] = React.useState<string | null>(
    initialGenerationId ?? null
  );

  const eventSourceRef = React.useRef<EventSource | null>(null);
  const reconnectTimerRef = React.useRef<number | null>(null);
  const reconnectAttemptRef = React.useRef(0);
  const sinceCursorRef = React.useRef<string | null>(null);
  const generationIdRef = React.useRef(generationId);
  generationIdRef.current = generationId;

  const closeStream = React.useCallback(() => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  // Connect to the SSE stream for a given generationId
  const connectStreamRef = React.useRef<((since: string | null) => void) | undefined>(undefined);

  React.useEffect(() => {
    if (!generationId) {
      closeStream();
      return;
    }

    let cancelled = false;

    const connectStream = (since: string | null) => {
      if (cancelled) return;

      const url = new URL(`${API_URL}/api-sequence-agent/stream`);
      url.searchParams.set('generationId', generationId);
      if (since) {
        url.searchParams.set('since', since);
      }

      const eventSource = new EventSource(url.toString(), { withCredentials: true });
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        reconnectAttemptRef.current = 0;
        setConnectionState('connected');
      };

      const handleEvent = (event: MessageEvent) => {
        try {
          const payload = JSON.parse(event.data) as {
            eventType: string;
            emittedAt: string;
            [key: string]: unknown;
          };

          // Track cursor for reconnection
          if (payload.emittedAt) {
            sinceCursorRef.current = payload.emittedAt;
          }

          // Map broker event to AgentEvent format
          const agentEvent: AgentEvent = {
            type: payload.eventType as AgentEventType,
            timestamp: payload.emittedAt ? Date.parse(payload.emittedAt) : Date.now(),
            toolName: payload.toolName as string | undefined,
            args: payload.args,
            result: payload.result,
            success: payload.success as boolean | undefined,
            delta: payload.delta as string | undefined,
            message: payload.message as string | undefined,
            config: payload.config as ApiSequenceConfig | undefined,
            summary: payload.summary as string | undefined,
            propertyId: payload.propertyId as string | null | undefined,
            credentialId: payload.credentialId as string | null | undefined,
          };

          setEvents((prev) => [...prev, agentEvent]);

          if (payload.eventType === 'complete') {
            setFinalConfig((payload.config as ApiSequenceConfig) ?? null);
            setSummary((payload.summary as string) ?? null);
            setStatus('complete');
          } else if (payload.eventType === 'error') {
            setStatus('error');
          }

          reconnectAttemptRef.current = 0;
          setConnectionState('connected');
        } catch (error) {
          console.error('Failed to parse API sequence generation event', error);
        }
      };

      eventSource.addEventListener('api-sequence-generation', handleEvent as EventListener);

      eventSource.onerror = () => {
        eventSource.close();
        if (cancelled) return;

        const nextAttempt = reconnectAttemptRef.current + 1;
        reconnectAttemptRef.current = nextAttempt;

        if (nextAttempt > MAX_RECONNECT_ATTEMPTS) {
          setConnectionState('failed');
          return;
        }

        setConnectionState('reconnecting');
        const backoffMs = Math.min(10000, 1000 * Math.pow(2, nextAttempt - 1));
        reconnectTimerRef.current = window.setTimeout(() => {
          connectStream(sinceCursorRef.current);
        }, backoffMs);
      };
    };

    connectStreamRef.current = connectStream;
    setStatus('running');
    connectStream(sinceCursorRef.current);

    return () => {
      cancelled = true;
      closeStream();
    };
  }, [generationId, closeStream]);

  const startGeneration = React.useCallback(
    (options: StartGenerateOptions) => {
      // Reset state for new generation
      setEvents([]);
      setFinalConfig(null);
      setSummary(null);
      setStatus('running');
      sinceCursorRef.current = null;
      reconnectAttemptRef.current = 0;
      closeStream();

      const run = async () => {
        try {
          const response = await fetch(`${API_URL}${options.endpoint}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(options.body),
          });

          if (!response.ok) {
            const contentType = response.headers.get('content-type') ?? '';
            let message: string | null = null;

            if (contentType.includes('application/json')) {
              try {
                const payload = (await response.json()) as { error?: unknown };
                if (typeof payload.error === 'string' && payload.error.trim().length > 0) {
                  message = payload.error;
                }
              } catch {
                // Fall through to raw text
              }
            }

            if (!message) {
              const text = await response.text();
              message = text || `HTTP ${response.status}`;
            }

            setEvents((prev) => [
              ...prev,
              { type: 'error', timestamp: Date.now(), message },
            ]);
            setStatus('error');
            return;
          }

          const result = (await response.json()) as { ok: boolean; generationId?: string };
          if (result.ok && result.generationId) {
            // Setting generationId triggers the useEffect to connect the stream
            setGenerationId(result.generationId);
          } else {
            setEvents((prev) => [
              ...prev,
              { type: 'error', timestamp: Date.now(), message: 'Failed to start generation' },
            ]);
            setStatus('error');
          }
        } catch (err) {
          setEvents((prev) => [
            ...prev,
            {
              type: 'error',
              timestamp: Date.now(),
              message: err instanceof Error ? err.message : String(err),
            },
          ]);
          setStatus('error');
        }
      };

      void run();
    },
    [closeStream]
  );

  const cancel = React.useCallback(() => {
    closeStream();
    setGenerationId(null);
    setStatus('idle');
  }, [closeStream]);

  const reset = React.useCallback(() => {
    closeStream();
    setGenerationId(null);
    setEvents([]);
    setStatus('idle');
    setFinalConfig(null);
    setSummary(null);
    sinceCursorRef.current = null;
    reconnectAttemptRef.current = 0;
  }, [closeStream]);

  return {
    events,
    status,
    finalConfig,
    summary,
    connectionState,
    startGeneration,
    cancel,
    reset,
  };
}
